/**
 * Name resolution for the ids purchasing records carry. Every lookup is
 * permission-gated by the caller: without the cross-domain read grant the
 * screen falls back to the raw id rather than firing a query that 403s.
 */
import { useCallback } from "react";

import {
  useSupplierOptions,
  useVariantOptions,
  useWarehouseOptions,
  useUomOptions,
} from "../queries";

export function useSupplierNames(enabled = true) {
  const query = useSupplierOptions("", enabled);
  const parties = query.data;
  const supplierName = useCallback(
    (supplierId: string | undefined): string => {
      if (supplierId === undefined) return "";
      return parties?.find((party) => party.id === supplierId)?.display_name ?? supplierId;
    },
    [parties],
  );
  return { supplierName, isLoading: query.isLoading, parties: parties ?? [] };
}

export function useVariantSkus(enabled = true) {
  const query = useVariantOptions("", enabled);
  const variants = query.data;
  const variantSku = useCallback(
    (variantId: string | undefined): string => {
      if (variantId === undefined) return "";
      return variants?.find((variant) => variant.id === variantId)?.sku ?? variantId;
    },
    [variants],
  );
  const variantName = useCallback(
    (variantId: string | undefined): string | undefined =>
      variantId === undefined
        ? undefined
        : variants?.find((variant) => variant.id === variantId)?.name,
    [variants],
  );
  return { variantSku, variantName, variants: variants ?? [], isLoading: query.isLoading };
}

export function useWarehouseNames(enabled = true) {
  const query = useWarehouseOptions(enabled);
  const warehouses = query.data;
  const warehouseName = useCallback(
    (warehouseId: string | undefined): string => {
      if (warehouseId === undefined) return "";
      return warehouses?.find((warehouse) => warehouse.id === warehouseId)?.name ?? warehouseId;
    },
    [warehouses],
  );
  /**
   * Null when the warehouse is not one the acting branch can read. Warehouses
   * are branch-scoped and company-wide work (a reorder run) crosses branches,
   * so a screen that shows those rows must be able to say "not from here"
   * instead of printing an id at somebody.
   */
  const knownWarehouseName = useCallback(
    (warehouseId: string | undefined): string | null =>
      warehouses?.find((warehouse) => warehouse.id === warehouseId)?.name ?? null,
    [warehouses],
  );
  return {
    warehouseName,
    knownWarehouseName,
    warehouses: warehouses ?? [],
    isLoading: query.isLoading,
  };
}

export function useUomCodes(enabled = true) {
  const query = useUomOptions(enabled);
  const uoms = query.data;
  const uomCode = useCallback(
    (uomId: string | undefined): string => {
      if (uomId === undefined) return "";
      return uoms?.find((uom) => uom.id === uomId)?.code ?? uomId;
    },
    [uoms],
  );
  return { uomCode, uoms: uoms ?? [], isLoading: query.isLoading };
}
